/**
 * Rounds a cents amount to the nearest cent the same way the API does:
 * accounting-js's toFixed(cents / 100, 2) * 100. Copied rather than imported
 * so this file stays import-free for orgOwnerCheckoutGst.check.mjs.
 *
 * @param {number} cents
 * @returns {number}
 */
export function roundToCent(cents) {
  return Number((Math.round((cents / 100 + 1e-8) * 100) / 100).toFixed(2)) * 100;
}

/**
 * The org-owner checkout's GST, worked out the way the API works out the
 * order's GST (storeTransformer.js's formatOrderTransformer), so the checkout
 * shows what the order is charged. All amounts are in cents.
 *
 * GST base: the taxable subtotal (after discounts/promos) plus delivery and
 * any card fee, less a pre-GST voucher. The API takes the whole voucher off
 * the taxable base, as if it came off taxable items first, even on a cart with
 * GST-free items; this does the same on purpose so the two agree.
 *
 * The payment efficiency benefit is calculated on the whole merchandise base,
 * GST-free items included, so only its taxable share reduces GST. That share
 * is taken against the taxable subtotal after the voucher, because the
 * benefit's base has the voucher taken off too.
 *
 * Each part is rounded on its own, as the API does, before they are added.
 * The API also rounds each line's GST first, so this can still be a cent out
 * on some multi-line carts.
 *
 * @param {Object} options
 * @param {number} options.taxableSubtotal - Taxable items' subtotal, before GST.
 * @param {number} options.delivery - Delivery charge.
 * @param {number} options.cardFee - Card surcharge, 0 when none.
 * @param {number} options.benefit - Payment efficiency benefit amount.
 * @param {number} options.benefitBase - The base the benefit was calculated on.
 * @param {number} options.preGSTVoucher - A voucher applied before GST, 0 when none.
 * @returns {number} GST in cents, never negative.
 */
export function orgOwnerCheckoutGST({
  taxableSubtotal = 0,
  delivery = 0,
  cardFee = 0,
  benefit = 0,
  benefitBase = 0,
  preGSTVoucher = 0,
}) {
  // Nothing taxable and no delivery: a card fee alone is not taxed.
  if (!taxableSubtotal && !delivery) return 0;

  const taxableAfterVoucher = Math.max(0, taxableSubtotal - preGSTVoucher);
  const taxableShareOfBenefit = benefitBase > 0 ? benefit * (taxableAfterVoucher / benefitBase) : 0;

  const cartGST = roundToCent((taxableSubtotal + delivery - preGSTVoucher) * 0.1);
  const cardFeeGST = roundToCent(cardFee * 0.1);
  const benefitGST = roundToCent(taxableShareOfBenefit * 0.1);

  return roundToCent(Math.max(0, cartGST + cardFeeGST - benefitGST));
}
